/**
 * LoadingState — Phase 13.3 (2026-04-29) primitive
 *
 * Reusable loading indicator with three variants:
 *   - skeleton:  for known-layout lists (1-10s) — shimmer rectangles
 *   - spinner:   for blocking actions (<3s) — single rotating disk
 *   - progress:  for known progress (>3s) — determinate bar 0-1
 *
 * Per external research (Phase 13 scope §2 13.3.1):
 *   - aria-busy + aria-live="polite" + role="status"
 *   - Match final layout dimensions (no CLS / layout shift)
 *   - prefers-reduced-motion respected (CSS handles via .juuni-loading-shimmer)
 *
 * Replaces 11 bare `<div>Loading...</div>` instances across the app.
 */
// Phase 18 audit-fix (2026-04-29): removed PropTypes import — codebase
// doesn't use PropTypes elsewhere (transitive dep only). JSDoc above
// serves as the type contract.
export default function LoadingState({
  kind = 'spinner',
  rows = 3,
  message,
  progress,
  ariaLabel,
  style: extraStyle = {},
}) {
  const baseAttrs = {
    role: 'status',
    'aria-live': 'polite',
    'aria-busy': true,
    'aria-label': ariaLabel || message || 'Loading',
  };

  if (kind === 'skeleton') {
    return (
      <div {...baseAttrs} style={{ ...skeletonContainerStyle, ...extraStyle }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} style={skeletonRowStyle} className="juuni-loading-shimmer" />
        ))}
      </div>
    );
  }

  if (kind === 'progress') {
    const pct = Math.max(0, Math.min(1, Number(progress) || 0));
    return (
      <div {...baseAttrs} style={{ ...progressContainerStyle, ...extraStyle }}>
        <div style={progressTrackStyle}>
          <div style={{ ...progressFillStyle, width: `${pct * 100}%` }} />
        </div>
        {message && <div style={progressMessageStyle}>{message}</div>}
      </div>
    );
  }

  // spinner (default)
  return (
    <div {...baseAttrs} style={{ ...spinnerContainerStyle, ...extraStyle }}>
      <div style={spinnerStyle} className="juuni-loading-spin" />
      {message && <div style={spinnerMessageStyle}>{message}</div>}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const skeletonContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 16,
};

const skeletonRowStyle = {
  height: 16,
  background: 'linear-gradient(90deg, rgba(0,0,0,0.06), rgba(0,0,0,0.10), rgba(0,0,0,0.06))',
  backgroundSize: '200% 100%',
  borderRadius: 6,
};

const spinnerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 24,
};

const spinnerStyle = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '3px solid rgba(0,0,0,0.10)',
  borderTopColor: 'var(--accent-teal, #5DCAA5)',
};

const spinnerMessageStyle = {
  fontSize: 12,
  color: 'var(--text-muted, #8e8575)',
  fontFamily: 'var(--font-mono, monospace)',
  letterSpacing: '0.04em',
};

const progressContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 16,
};

const progressTrackStyle = {
  height: 6,
  borderRadius: 3,
  background: 'rgba(0,0,0,0.06)',
  overflow: 'hidden',
};

const progressFillStyle = {
  height: '100%',
  background: 'var(--accent-teal, #5DCAA5)',
  transition: 'width 0.2s ease',
};

const progressMessageStyle = {
  fontSize: 12,
  color: 'var(--text-muted, #8e8575)',
  fontFamily: 'var(--font-mono, monospace)',
};
